import * as React from 'react';
import { observer } from 'mobx-react';
import { ServiceRequestDataPoint } from '../store/schema/DiagnosticsLogState';
import { DiagnosticsLogger } from '../store/schema/DiagnosticsLogState';
import {
    DetailsList,
    ConstrainMode,
    Selection,
    SelectionMode,
    DetailsListLayoutMode,
    IColumn,
} from 'office-ui-fabric-react/lib/components/DetailsList';
import { selectServiceDiagnosticsItem } from '../actions/serviceRequestDiagnostics';
import { Inspector, chromeLight } from 'react-inspector';

export interface NetworkDiagnosticsPanelProps {
    logger: DiagnosticsLogger<ServiceRequestDataPoint>;
}

const styles = require('./DiagnosticsPanel.scss');

const inspectorTheme = {
    ...chromeLight,
    TREENODE_FONT_FAMILY: 'Menlo, Consolas, monospace',
    TREENODE_FONT_SIZE: '12px',
    OBJECT_NAME_COLOR: 'rgb(180, 0, 158)',
    OBJECT_VALUE_NULL_COLOR: 'rgb(102, 102, 102)',
    OBJECT_VALUE_UNDEFINED_COLOR: 'rgb(102, 102, 102)',
    OBJECT_VALUE_REGEXP_COLOR: 'rgb(232, 17, 35)',
    OBJECT_VALUE_STRING_COLOR: 'rgb(232, 17, 35)',
    OBJECT_VALUE_SYMBOL_COLOR: 'rgb(232, 17, 35)',
    OBJECT_VALUE_NUMBER_COLOR: 'rgb(0, 120, 215)',
    OBJECT_VALUE_BOOLEAN_COLOR: 'rgb(0, 120, 215)',
    OBJECT_VALUE_FUNCTION_KEYWORD_COLOR: 'rgb(216, 59, 1)',
};

@observer
export default class NetworkDiagnosticsPanel extends React.Component<NetworkDiagnosticsPanelProps> {
    private columns: IColumn[] = [
        {
            name: 'Action',
            fieldName: 'action',
            key: 'action',
            minWidth: 150,
        },

        {
            name: 'Status',
            fieldName: 'status',
            key: 'status',
            minWidth: 50,
        },

        {
            name: 'Start',
            fieldName: 'start',
            key: 'start',
            minWidth: 100,
        },

        {
            name: 'BE Time (ms)',
            fieldName: 'serverTime',
            key: 'serverTime',
            minWidth: 100,
        },

        {
            name: 'AD Latency (ms)',
            fieldName: 'adTime',
            key: 'adTime',
            minWidth: 100,
        },

        {
            name: 'RPC Latency (ms)',
            fieldName: 'rpcTime',
            key: 'rpcTime',
            minWidth: 100,
        },

        {
            name: 'Total (ms)',
            fieldName: 'total',
            key: 'total',
            minWidth: 100,
        },

        {
            name: 'Action Source',
            fieldName: 'actionSource',
            key: 'actionSource',
            minWidth: 150,
        },

        {
            name: 'Request ID',
            fieldName: 'requestId',
            key: 'requestId',
            minWidth: 250,
        },
    ];

    private getKey = item => item.requestId;

    private selection = new Selection({
        onSelectionChanged: () => {
            const selectionCount = this.selection.getSelectedCount();
            const key =
                selectionCount > 0
                    ? (this.selection.getSelection()[0] as ServiceRequestDataPoint).requestId
                    : null;
            selectServiceDiagnosticsItem(key);
        },
        canSelectItem: () => true,
        selectionMode: SelectionMode.single,
        getKey: this.getKey,
    });

    private renderItemColumn = (item, index, column) => {
        return <span>{item[column.key]}</span>;
    };

    private getActionSourceFromRequestHeader(headers: any): string {
        return headers ? headers['x-owa-actionsource'] : '';
    }

    render() {
        const { datapoints, selected } = this.props.logger;

        let items: any = {};
        if (datapoints) {
            items = datapoints.map(datapoint => {
                return {
                    requestId: datapoint.requestId,
                    action: datapoint.action,
                    actionSource: this.getActionSourceFromRequestHeader(datapoint.request.headers),
                    status: datapoint.request.status,
                    start: datapoint.timing.start,
                    serverTime: datapoint.timing.serverTime,
                    adTime: datapoint.timing.adTime,
                    rpcTime: datapoint.timing.rpcTime,
                    total: datapoint.timing.total,
                };
            });
        }

        return (
            <div className={styles.container}>
                <div className={styles.table}>
                    <DetailsList
                        items={items}
                        columns={this.columns}
                        compact={true}
                        constrainMode={ConstrainMode.unconstrained}
                        setKey="serviceRequest"
                        layoutMode={DetailsListLayoutMode.fixedColumns}
                        key="serviceRequest"
                        selection={this.selection}
                        selectionMode={SelectionMode.single}
                        selectionPreservedOnEmptyClick={true}
                        onRenderItemColumn={this.renderItemColumn}
                    />
                </div>
                {selected && (
                    <div className={styles.details}>
                        <div>
                            <h3>Summary</h3>
                            <Inspector data={selected.request} theme={inspectorTheme} />
                        </div>
                    </div>
                )}
            </div>
        );
    }
}
